// Task Completion Trend section — dashboard parity v3.6.
//
// v1's "Average Task Completion Time" line chart. Buckets done
// Commitments by month and renders the average days-to-complete
// (``completed_at - created_at``) as a sparkline-style line over
// the last N months (default 6). Pairs nicely with the Delivery
// Performance chart above it — Delivery answers "are we hitting
// the dates we promised?" while Trend answers "how long did the
// work take, regardless of the date?".

import { useMemo, useState } from "react";

import {
  DashboardCard,
  DashboardEmptyState,
  DashboardErrorState,
  DashboardLoadingState,
  useTaskCompletionTrendQuery,
} from "@/features/dashboard";


export const TASK_COMPLETION_TREND_SECTION = "task-completion-trend" as const;


const RANGE_OPTIONS: Array<{ months: number; label: string }> = [
  { months: 3, label: "3 mo" },
  { months: 6, label: "6 mo" },
  { months: 12, label: "12 mo" },
];


export function TaskCompletionTrendSection() {
  const [months, setMonths] = useState(6);
  const query = useTaskCompletionTrendQuery({ months });

  const series = query.data?.series ?? [];
  const points = useMemo(
    () =>
      series.map((p) => ({
        ...p,
        value: p.average_days ?? null,
      })),
    [series],
  );

  // For SVG scaling.
  const filledValues = points
    .map((p) => p.value)
    .filter((v): v is number => v != null);
  const minValue = filledValues.length > 0 ? Math.min(...filledValues) : 0;
  const maxValue = filledValues.length > 0 ? Math.max(...filledValues) : 0;
  const span = Math.max(1, maxValue - minValue);

  return (
    <DashboardCard
      section={TASK_COMPLETION_TREND_SECTION}
      title="Average completion time"
      subtitle="Mean days from created to done, bucketed by month."
      actions={
        <div className="inline-flex rounded-md border border-border-subtle bg-canvas/40 p-0.5">
          {RANGE_OPTIONS.map((o) => (
            <button
              key={o.months}
              type="button"
              onClick={() => setMonths(o.months)}
              data-active={months === o.months ? "true" : "false"}
              className={[
                "rounded px-2 py-0.5 text-[11px] font-medium transition-fast ease-out",
                months === o.months
                  ? "bg-teal-tint text-text-primary"
                  : "text-text-secondary hover:text-text-primary",
              ].join(" ")}
            >
              {o.label}
            </button>
          ))}
        </div>
      }
    >
      {query.isLoading ? (
        <DashboardLoadingState variant="card" rows={3} />
      ) : query.isError ? (
        <DashboardErrorState
          title="Couldn't load completion trend"
          retry={() => query.refetch()}
        />
      ) : filledValues.length === 0 ? (
        <DashboardEmptyState
          title="No completed Commitments yet in this window"
          description="As Commitments close out, the average days-to-complete trend fills in here."
        />
      ) : (
        <div className="flex flex-col gap-3">
          <Sparkline points={points} minValue={minValue} span={span} />
          <ul
            role="list"
            data-testid="task-completion-trend-points"
            className="grid grid-cols-3 gap-2 sm:grid-cols-6"
          >
            {points.map((p) => (
              <li
                key={p.month}
                data-testid={`task-completion-trend-point-${p.month}`}
                className="flex flex-col gap-0.5 rounded-md bg-canvas/40 px-2 py-1.5"
              >
                <span className="text-[10px] uppercase tracking-wide text-text-tertiary">
                  {humanMonth(p.month)}
                </span>
                <span className="text-sm font-semibold tabular-nums text-text-primary">
                  {p.value != null ? `${p.value.toFixed(1)}d` : "—"}
                </span>
                <span className="text-[10px] text-text-tertiary">
                  {p.completed_count} done
                </span>
              </li>
            ))}
          </ul>
          {query.data?.overall_average_days != null && (
            <p className="text-[11px] text-text-tertiary">
              Overall window average: {query.data.overall_average_days.toFixed(1)} days
            </p>
          )}
        </div>
      )}
    </DashboardCard>
  );
}


function Sparkline({
  points, minValue, span,
}: {
  points: Array<{ month: string; value: number | null }>;
  minValue: number;
  span: number;
}) {
  const W = 600;
  const H = 80;
  const pad = 8;
  const usableW = W - pad * 2;
  const usableH = H - pad * 2;
  const step = points.length > 1 ? usableW / (points.length - 1) : 0;
  const scaled = points.map((p, i) => ({
    x: pad + i * step,
    y:
      p.value == null
        ? null
        : pad + usableH * (1 - (p.value - minValue) / span),
    month: p.month,
    value: p.value,
  }));
  // Build path skipping null gaps.
  const segments: string[] = [];
  let cursor: string[] = [];
  for (const pt of scaled) {
    if (pt.y == null) {
      if (cursor.length) segments.push(cursor.join(" "));
      cursor = [];
      continue;
    }
    cursor.push(
      cursor.length === 0
        ? `M${pt.x.toFixed(2)},${pt.y.toFixed(2)}`
        : `L${pt.x.toFixed(2)},${pt.y.toFixed(2)}`,
    );
  }
  if (cursor.length) segments.push(cursor.join(" "));
  return (
    <svg
      role="img"
      aria-label="Average completion time trend"
      data-testid="task-completion-trend-sparkline"
      viewBox={`0 0 ${W} ${H}`}
      className="h-20 w-full"
      preserveAspectRatio="none"
    >
      {segments.map((d, idx) => (
        <path
          key={idx}
          d={d}
          fill="none"
          stroke="#1F4E4A"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      ))}
      {scaled.map((pt) =>
        pt.y == null ? null : (
          <circle
            key={pt.month}
            cx={pt.x}
            cy={pt.y}
            r="2.5"
            fill="#1F4E4A"
          />
        ),
      )}
    </svg>
  );
}


function humanMonth(iso: string): string {
  // ``2026-04`` → ``Apr 26``
  const [y, m] = iso.split("-").map((s) => Number(s));
  if (!y || !m) return iso;
  const d = new Date(y, m - 1, 1);
  return d.toLocaleDateString(undefined, { month: "short", year: "2-digit" });
}
